import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import {
  Activity,
  ArrowDownCircle,
  ArrowUpCircle,
  Coins,
  DollarSign,
  Hash,
  Image,
} from 'lucide-react';
import {
  TaskyEmptyState,
  TaskyErrorState,
  TaskySpinner,
  TaskyStatCard,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../../tasky';
import adminUsageService, {
  OrganizationUsageResponse,
  PeriodType,
} from '../../../services/api/adminUsageService';

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFC658', '#FF6B6B', '#4ECDC4', '#45B7D1'
];

const PERIOD_OPTIONS: { value: PeriodType; label: string }[] = [
  { value: 'day', label: '今日' },
  { value: 'week', label: '過去7日間' },
  { value: 'month', label: '過去30日間' },
  { value: 'year', label: '過去1年間' },
];

type DisplayMode = 'credits' | 'tokens';

interface OrganizationUsageDashboardProps {
  organizationId: number;
}

const segmentButtonClass = (active: boolean) =>
  `px-3 py-1.5 rounded-tasky-xs text-sm transition-colors ${
    active ? 'bg-primary text-white font-medium' : 'text-gray-600 hover:bg-warm-hover'
  }`;

const chartCardClass = 'bg-white rounded-tasky-md border border-warm-border p-6';
const chartTitleClass = 'text-sm font-semibold text-dark mb-5';

const OrganizationUsageDashboard: React.FC<OrganizationUsageDashboardProps> = ({ organizationId }) => {
  const [data, setData] = useState<OrganizationUsageResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<PeriodType>('month');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('credits');

  useEffect(() => {
    fetchData();
  }, [period, organizationId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminUsageService.getOrganizationUsage(organizationId, period);
      if (response.success) {
        setData(response.data);
      } else {
        setError('データの取得に失敗しました');
      }
    } catch (err) {
      console.error('Failed to fetch organization usage:', err);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-12 text-sm text-gray-500">
        <TaskySpinner size="md" />
        読み込み中...
      </div>
    );
  }

  if (error) {
    return <TaskyErrorState description={error} />;
  }

  if (!data) {
    return <TaskyEmptyState title="データがありません" />;
  }

  return (
    <div>
      {/* Controls: Period Selector + Display Mode Toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="inline-flex items-center gap-1 bg-white rounded-tasky-sm border border-warm-border p-1">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setPeriod(option.value)}
              className={segmentButtonClass(period === option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1 bg-white rounded-tasky-sm border border-warm-border p-1">
          <button
            type="button"
            onClick={() => setDisplayMode('credits')}
            className={segmentButtonClass(displayMode === 'credits')}
          >
            クレジット
          </button>
          <button
            type="button"
            onClick={() => setDisplayMode('tokens')}
            className={segmentButtonClass(displayMode === 'tokens')}
          >
            トークン
          </button>
        </div>
      </div>

      {/* Organization Info */}
      <h3 className="text-base font-semibold text-dark mb-4">{data.organization.name}</h3>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <TaskyStatCard
          label="総リクエスト数"
          value={formatNumber(data.summary.total_requests)}
          icon={<Activity />}
          iconTone="primary"
        />
        {displayMode === 'credits' ? (
          <>
            <TaskyStatCard
              label="Taskyクレジット"
              value={formatNumber(Math.round(data.summary.total_credits || 0))}
              icon={<Coins />}
              iconTone="success"
            />
            <TaskyStatCard
              label="画像生成数"
              value={formatNumber(data.summary.total_image_count)}
              icon={<Image />}
              iconTone="warning"
            />
            <TaskyStatCard
              label="推定コスト"
              value={`$${data.summary.total_cost_usd.toFixed(2)}`}
              icon={<DollarSign />}
              iconTone="destructive"
            />
          </>
        ) : (
          <>
            <TaskyStatCard
              label="総トークン数"
              value={formatNumber(data.summary.total_tokens)}
              icon={<Hash />}
              iconTone="purple"
            />
            <TaskyStatCard
              label="入力トークン"
              value={formatNumber(data.summary.total_input_tokens)}
              icon={<ArrowDownCircle />}
              iconTone="purple"
            />
            <TaskyStatCard
              label="出力トークン"
              value={formatNumber(data.summary.total_output_tokens)}
              icon={<ArrowUpCircle />}
              iconTone="purple"
            />
            <TaskyStatCard
              label="画像生成数"
              value={formatNumber(data.summary.total_image_count)}
              icon={<Image />}
              iconTone="warning"
            />
            <TaskyStatCard
              label="推定コスト"
              value={`$${data.summary.total_cost_usd.toFixed(2)}`}
              icon={<DollarSign />}
              iconTone="destructive"
            />
          </>
        )}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Daily Trend Chart */}
        <div className={`${chartCardClass} lg:col-span-2`}>
          <h3 className={chartTitleClass}>{displayMode === 'credits' ? '日別クレジット使用量' : '日別トークン使用量'}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.daily_trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickFormatter={formatNumber}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                labelFormatter={(label) => new Date(label).toLocaleDateString('ja-JP')}
                formatter={(value: number) => [
                  formatNumber(displayMode === 'credits' ? Math.round(value) : value),
                  displayMode === 'credits' ? 'クレジット' : 'トークン'
                ]}
              />
              <Area
                type="monotone"
                dataKey={displayMode === 'credits' ? 'total_credits' : 'total_tokens'}
                stroke={displayMode === 'credits' ? '#00C49F' : '#0088FE'}
                fill={displayMode === 'credits' ? '#00C49F' : '#0088FE'}
                fillOpacity={0.3}
                name={displayMode === 'credits' ? 'クレジット' : 'トークン数'}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Feature Usage Pie Chart */}
        <div className={chartCardClass}>
          <h3 className={chartTitleClass}>{displayMode === 'credits' ? '機能別クレジット' : '機能別使用量'}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.by_feature as unknown as Record<string, unknown>[]}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey={displayMode === 'credits' ? 'total_credits' : 'total_tokens'}
                nameKey="feature_label"
                label={({ name, percent }: { name?: string; percent?: number }) =>
                  `${name || ''} (${((percent || 0) * 100).toFixed(0)}%)`
                }
                labelLine={false}
              >
                {data.by_feature.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => [
                  formatNumber(displayMode === 'credits' ? Math.round(value) : value),
                  displayMode === 'credits' ? 'クレジット' : 'トークン'
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* User Usage Bar Chart */}
        <div className={chartCardClass}>
          <h3 className={chartTitleClass}>{displayMode === 'credits' ? 'ユーザー別クレジット' : 'ユーザー別使用量'}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={data.by_user}
              layout="vertical"
              margin={{ left: 100 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                tickFormatter={formatNumber}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                type="category"
                dataKey="user_name"
                tick={{ fontSize: 12 }}
                width={100}
              />
              <Tooltip
                formatter={(value: number) => [
                  formatNumber(displayMode === 'credits' ? Math.round(value) : value),
                  displayMode === 'credits' ? 'クレジット' : 'トークン'
                ]}
              />
              <Bar
                dataKey={displayMode === 'credits' ? 'total_credits' : 'total_tokens'}
                fill={displayMode === 'credits' ? '#00C49F' : '#0088FE'}
                name={displayMode === 'credits' ? 'クレジット' : 'トークン数'}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* User Usage Table */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-dark mb-3">ユーザー別詳細</h3>
        <TaskyTable>
          <TaskyTableHead>
            <TaskyTableRow hoverable={false}>
              <TaskyTableHeaderCell>ユーザー名</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="right">リクエスト数</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="right">{displayMode === 'credits' ? 'クレジット' : 'トークン数'}</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="right">推定コスト</TaskyTableHeaderCell>
            </TaskyTableRow>
          </TaskyTableHead>
          <TaskyTableBody>
            {data.by_user.map((user, index) => (
              <TaskyTableRow key={index}>
                <TaskyTableCell className="font-medium">{user.user_name}</TaskyTableCell>
                <TaskyTableCell align="right">{formatNumber(user.total_requests)}</TaskyTableCell>
                <TaskyTableCell align="right">
                  {formatNumber(displayMode === 'credits' ? Math.round(user.total_credits || 0) : user.total_tokens)}
                </TaskyTableCell>
                <TaskyTableCell align="right">${user.total_cost_usd.toFixed(4)}</TaskyTableCell>
              </TaskyTableRow>
            ))}
          </TaskyTableBody>
        </TaskyTable>
      </div>

      {/* Feature Usage Table */}
      <div>
        <h3 className="text-sm font-semibold text-dark mb-3">機能別詳細</h3>
        <TaskyTable>
          <TaskyTableHead>
            <TaskyTableRow hoverable={false}>
              <TaskyTableHeaderCell>機能</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="right">リクエスト数</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="right">{displayMode === 'credits' ? 'クレジット' : 'トークン数'}</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="right">推定コスト</TaskyTableHeaderCell>
            </TaskyTableRow>
          </TaskyTableHead>
          <TaskyTableBody>
            {data.by_feature.map((feature, index) => (
              <TaskyTableRow key={index}>
                <TaskyTableCell className="font-medium">{feature.feature_label}</TaskyTableCell>
                <TaskyTableCell align="right">{formatNumber(feature.total_requests)}</TaskyTableCell>
                <TaskyTableCell align="right">
                  {formatNumber(displayMode === 'credits' ? Math.round(feature.total_credits || 0) : feature.total_tokens)}
                </TaskyTableCell>
                <TaskyTableCell align="right">${feature.total_cost_usd.toFixed(4)}</TaskyTableCell>
              </TaskyTableRow>
            ))}
          </TaskyTableBody>
        </TaskyTable>
      </div>
    </div>
  );
};

export default OrganizationUsageDashboard;
